import {
  EAiActionStatus,
  EDatasourceType,
  ESdChangeReason,
  IActionDefinitionDto,
  IAiModel,
  IAiTemplate
} from "@kuika/kuika-cl-model";
import _ from "lodash";
import { DatasourceService } from "../../../../services/datasource-service";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { PureSdComponent } from "../../../../shared/pure-sd";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import { DeleteModal } from "../../../common/delete-modal";
import KuikaBadge from "../../../ui-kit/kuika-badge/kuika-badge";
import KuikaButton from "../../../ui-kit/kuika-button/kuika-button";
import KuikaFilterChips, { IKuikaFilterChipOption } from "../../../ui-kit/kuika-filter-chips/kuika-filter-chips";
import KuikaInput from "../../../ui-kit/kuika-input/kuika-input";
import {
  KuikaCreateCard,
  KuikaCreateCardRow,
  KuikaListPageHeader,
  KuikaListPageToolbar
} from "../../../ui-kit/kuika-list-page/kuika-list-page";
import KuikaListTable, {
  KuikaListTableColumn,
  KuikaListTableNameCell,
  KuikaListTableRowAction,
  KuikaListTableStatusCell,
  KuikaListTableUserCell
} from "../../../ui-kit/kuika-list-table/kuika-list-table";
import KuikaSelectbox from "../../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaTabContent from "../../../ui-kit/kuika-tab-content/kuika-tab-content";
import KuikaTabs from "../../../ui-kit/kuika-tabs/kuika-tabs";
import KuikaTooltip from "../../../ui-kit/kuika-tooltip/kuika-tooltip";
import CreateScratchEmptyState from "../common/create-scratch-empty-state/create-scratch-empty-state";
import { AIActionCustomTemplate } from "./ai-action-custom-template";
import "./ai-action-template-detail-modal.scss";
import AIActionTemplates from "./ai-action-templates";
import "./ai-action.scss";

export interface IAiActionPanelState {
  actionToDelete: IActionDefinitionDto | null;
  aiModels: IAiModel[];
  aiTemplates: IAiTemplate[];
  isDeleteActionModalVisible: boolean;
  isTemplatesLoading: boolean;
  selectedAIModelIds: string[];
  searchTerm: string;
  selectedTags: string[];
  isScratchSidebarVisible: boolean;
  isScratchEmptyStateVisible: boolean;
  activeTab: "templates" | "recents";
}

export interface IAiActionPanelProps {
  isCustomTemplateOnInit: boolean;
  form?: any;
  isModal: boolean;
  selectedTemplateId?: string;
  isCreatingCustomTemplate: boolean;
  isEditing?: boolean;
  isEditingTemplate?: boolean;
  model: string;
  returnType: "text" | "json";
  onModelChange: (model: string) => void;
  onReturnTypeChange: (returnType: "json" | "text") => void;
  onTemplateChange: (value: string) => void;
  onAIPromptChange: (value: string) => void;
  setTemplateSelected: (modelId: string, name: string, template?: any) => void;
  requestPromptsFromAI: (value: string) => void;
  onClose?: () => void;
  onEdit?: (templateId: string) => void;
  onCreateTemplate?: (modelId: string, name: string, template: any) => void;
  onStartBuilding?: () => void;
  isCreatingFromTemplate?: boolean;
}

/** The tabs in their on-screen order, so a switch slides towards the one chosen. */
const AI_ACTION_TAB_ORDER: string[] = ["templates", "recents"];

class AiActionPanel extends PureSdComponent<IAiActionPanelProps & IWithSDProps, IAiActionPanelState> {
  scratchFeatures: string[] = [
    "Full control over prompts and logic",
    "Choose any supported AI model",
    "Customize output format",
    "Built-in AI assistance"
  ];

  constructor(props: IAiActionPanelProps & IWithSDProps) {
    super(props);
    this.state = {
      actionToDelete: null,
      aiModels: [],
      aiTemplates: [],
      isDeleteActionModalVisible: false,
      isTemplatesLoading: false,
      selectedAIModelIds: [],
      searchTerm: "",
      selectedTags: [],
      isScratchSidebarVisible: false,
      isScratchEmptyStateVisible: false,
      activeTab: "templates"
    };
  }

  componentDidMount = async () => {
    this.setState({ isTemplatesLoading: true });
    try {
      const aiModelResponse = await DatasourceService.GetAiModels();
      const aiTemplatesResponse = await DatasourceService.GetAiTemplates();
      const aiModels: IAiModel[] = aiModelResponse.data;
      const aiTemplates: IAiTemplate[] = aiTemplatesResponse.data;
      this.setState({ aiTemplates });
      this.setState({ aiModels });
    } catch (error) {
      KMainFunctions.displayWarningNotification("Failed to load AI models");
    } finally {
      this.setState({ isTemplatesLoading: false });
    }
  };

  handleFilterTemplates = (selectedAIModelIds: string[]) => {
    this.setState({
      selectedAIModelIds
    });
  };

  handleSearchTemplates = (searchTerm: string) => {
    this.setState({
      searchTerm
    });
  };

  handleFilterByTags = (selectedTags: string[]) => {
    this.setState({
      selectedTags
    });
  };

  handleClearFilters = () => {
    this.setState({
      selectedAIModelIds: [],
      selectedTags: [],
      searchTerm: ""
    });
  };

  getFilteredTemplates = (): IAiTemplate[] => {
    const filtered: IAiTemplate[] = this.getTemplatesBeforeTagFilter();

    // Filter by selected tags. OR, so a template carrying two selected categories is
    // matched by either and still listed once.
    if (this.state.selectedTags?.length) {
      return filtered.filter((template) => {
        const templateTags = (template as any).tags || [];
        return this.state.selectedTags.some((tag) => templateTags.includes(tag));
      });
    }

    return filtered;
  };

  /** Everything except the category step — what the chips count against. */
  getTemplatesBeforeTagFilter = (): IAiTemplate[] => {
    let filtered = this.state.aiTemplates;

    // Filter by selected models
    if (this.state.selectedAIModelIds?.length) {
      filtered = filtered.filter((template) => this.state.selectedAIModelIds.includes(template.modelId));
    }

    // Filter by search term (case-insensitive search in name and description)
    // Normalize by removing spaces to match CamelCase names (e.g., "Cv Reader" matches "CvReader")
    if (this.state.searchTerm) {
      const searchNormalized = this.state.searchTerm.toLowerCase().replace(/\s+/g, "");
      filtered = filtered.filter(
        (template) =>
          template.name.toLowerCase().replace(/\s+/g, "").includes(searchNormalized) ||
          template.description.toLowerCase().includes(this.state.searchTerm.toLowerCase())
      );
    }

    return filtered;
  };

  /**
   * Category chips, ordered by how much they hold. Counts come from the set the OTHER
   * filters have already narrowed, so a chip never advertises results that clicking it
   * would not return — and a category emptied by the current search drops out entirely
   * rather than sitting there as a dead end.
   */
  getCategoryOptions = (): IKuikaFilterChipOption[] => {
    const isTemplatesTab: boolean = this.state.activeTab === "templates";
    const source: { tags: string[] }[] = isTemplatesTab
      ? this.getTemplatesBeforeTagFilter().map((template: IAiTemplate) => ({
          tags: ((template as any).tags || []) as string[]
        }))
      : this.getRecentsBeforeTagFilter().map((action: IActionDefinitionDto) => ({ tags: this.getActionTags(action) }));

    const counts: Map<string, number> = new Map();
    source.forEach((template: { tags: string[] }) => {
      const templateTags: string[] = template.tags || [];
      // A template tagged twice with the same value must not count twice.
      Array.from(new Set(templateTags)).forEach((tag: string) => counts.set(tag, (counts.get(tag) ?? 0) + 1));
    });
    return Array.from(counts.entries())
      .sort((a: [string, number], b: [string, number]) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .map(([tag, count]: [string, number]) => ({ label: tag, value: tag, count }));
  };

  getUniqueModels = (): IAiModel[] => {
    return this.state.aiModels.filter((model) =>
      this.state.aiTemplates.some((template) => template.modelId === model.id)
    );
  };

  setTemplateSelected = (modelId: string, name: string, template?: any) => {
    this.props.setTemplateSelected(modelId, name, template);
  };

  openDeleteActionModal = (action: IActionDefinitionDto) => {
    this.setState({ actionToDelete: action, isDeleteActionModalVisible: true });
  };

  getAIActions = (): IActionDefinitionDto[] => {
    return (this.props.sd?.actionDefinitions || []).filter((a) => a.actionType === EDatasourceType.AIActions);
  };

  private normalizeSearchValue = (value?: string): string => {
    if (!value) return "";

    return value
      .toLowerCase()
      .replace(/[\s_-]+/g, "")
      .trim();
  };

  private getActionTemplate = (record: IActionDefinitionDto): IAiTemplate | undefined => {
    const aiInfo = this.props.sd.getStringifiedAiActionInfo(record.aiActionInfo);
    const templateId = aiInfo?.templateId;
    if (!templateId) return undefined;
    return this.state.aiTemplates.find((template) => template.id === templateId);
  };

  private getActionModelId = (record: IActionDefinitionDto): string => {
    const aiInfo = this.props.sd.getStringifiedAiActionInfo(record.aiActionInfo);
    const rawModel = aiInfo?.model;
    const rawModelId = typeof rawModel === "string" ? rawModel : "";

    const matchedModel = rawModelId
      ? this.state.aiModels.find((model) => model.id === rawModelId || model.name === rawModelId)
      : undefined;

    if (matchedModel?.id || rawModelId) {
      return matchedModel?.id ?? rawModelId;
    }

    const matchedTemplate = this.getActionTemplate(record);
    return matchedTemplate?.modelId || "";
  };

  private getActionTags = (record: IActionDefinitionDto): string[] => {
    const matchedTemplate = this.getActionTemplate(record);
    return ((matchedTemplate as any)?.tags || []) as string[];
  };

  private getRecentsModelOptions = () => {
    const modelIds = new Set<string>();
    this.getAIActions().forEach((action) => {
      const modelId = this.getActionModelId(action);
      if (modelId) modelIds.add(modelId);
    });

    return Array.from(modelIds).map((modelId) => {
      const model = this.state.aiModels.find((m) => m.id === modelId || m.name === modelId);
      return {
        label: model?.name || modelId,
        value: model?.id || modelId
      };
    });
  };

  private getFilteredRecentActions = (): IActionDefinitionDto[] => {
    const filtered: IActionDefinitionDto[] = this.getRecentsBeforeTagFilter();

    if (this.state.selectedTags.length > 0) {
      return filtered.filter((action) => {
        const actionTags = this.getActionTags(action);
        return this.state.selectedTags.some((tag) => actionTags.includes(tag));
      });
    }

    return filtered;
  };

  /** Everything except the category step — what the chips count against on Recents. */
  private getRecentsBeforeTagFilter = (): IActionDefinitionDto[] => {
    let filtered = this.getAIActions();

    if (this.state.searchTerm) {
      const normalizedSearch = this.normalizeSearchValue(this.state.searchTerm);
      filtered = filtered.filter((action) => this.normalizeSearchValue(action.name).includes(normalizedSearch));
    }

    if (this.state.selectedAIModelIds.length > 0) {
      filtered = filtered.filter((action) => {
        const modelId = this.getActionModelId(action);
        return !!modelId && this.state.selectedAIModelIds.includes(modelId);
      });
    }

    return filtered;
  };

  private applyRecentsTail = (filtered: IActionDefinitionDto[]): IActionDefinitionDto[] => {
    return filtered;
  };

  hasCreatedAIActionBefore = (): boolean => {
    return this.getAIActions().length > 0;
  };

  closeScratchEmptyState = () => {
    this.setState({ isScratchEmptyStateVisible: false });
  };

  startBuildingFromScratch = () => {
    this.setTemplateSelected("custom", "AI_Action");
    this.setState({
      isScratchSidebarVisible: false,
      isScratchEmptyStateVisible: false
    });
    this.props.onStartBuilding?.();
  };

  handleCreateFromScratchClick = () => {
    if (this.hasCreatedAIActionBefore()) {
      this.setState({
        isScratchSidebarVisible: true,
        isScratchEmptyStateVisible: false
      });
      return;
    }

    this.setState({
      isScratchEmptyStateVisible: true,
      isScratchSidebarVisible: false
    });
  };

  private formatModelName = (name?: string): string => {
    if (!name) return "Unknown Model";

    const normalized = name.replace(/[-_]+/g, " ").trim();

    const tokens = normalized.split(/\s+/).map((token) => {
      if (/^[0-9.]+$/.test(token)) return token;

      if (/^[a-zA-Z]{1,3}$/.test(token)) return token.toUpperCase();

      const match = token.match(/^([a-zA-Z]+)(.*)$/);
      if (match) {
        const alpha = match[1];
        const rest = match[2];
        if (alpha.length <= 3) {
          return alpha.toUpperCase() + rest;
        }

        return alpha[0].toUpperCase() + alpha.slice(1).toLowerCase() + rest;
      }

      return token[0].toUpperCase() + token.slice(1).toLowerCase();
    });

    return tokens.join(" ");
  };

  getFormattedAIActionModel = (record: IActionDefinitionDto): string => {
    const aiInfo = this.props.sd.getStringifiedAiActionInfo(record.aiActionInfo);
    const rawModel = aiInfo?.model;
    const templateId = aiInfo?.templateId;

    const matchedModel = rawModel
      ? this.state.aiModels.find((model) => model.id === rawModel || model.name === rawModel)
      : undefined;

    if (matchedModel?.name || rawModel) {
      return this.formatModelName(matchedModel?.name || rawModel);
    }

    if (!templateId) return "";

    const matchedTemplate = this.state.aiTemplates.find((template) => template.id === templateId);
    const templateModel = this.state.aiModels.find((model) => model.id === matchedTemplate?.modelId);

    return this.formatModelName(templateModel?.name);
  };

  getStatusBadge = (record: IActionDefinitionDto) => {
    const status = record.aiActionStatus ?? EAiActionStatus.Published;
    if (status === EAiActionStatus.Draft) {
      return <KuikaBadge content="Draft" color="yellow" />;
    }
    return <KuikaBadge content="Published" color="green" />;
  };

  deleteAIAction = async () => {
    if (!this.state.actionToDelete) return;

    try {
      const res = await DatasourceService.DeleteAction(this.state.actionToDelete);

      if (res && res.status === 200) {
        if (this.props.sd.editingAction && this.props.sd.editingAction.id === this.state.actionToDelete.id) {
          this.props.sd.cancelCreatingOrEditingDatasource();
          this.props.sd.setCreateNotificationTemplateActive();
        }

        this.props.sd.loadActionDefinitions();
        this.props.sd.refreshActions();
        KMainFunctions.displaySuccessNotification("Action deleted successfully");
      }
    } catch (error) {
      KMainFunctions.displayErrorNotification(error);
    } finally {
      this.setState({
        actionToDelete: null,
        isDeleteActionModalVisible: false
      });
    }
  };

  /** Opens an action in its editor, keeping the row-menu behaviour it replaces. */
  handleEditRecentAction = (record: IActionDefinitionDto) => {
    const ds = this.props.sd.datasources.find((d) => d.id === record.kDatasource_Id);
    if (ds) this.props.sd.setEditingActionsDatasource(ds, record, false, false);
  };

  renderRecentsTable = () => {
    const aiActions: IActionDefinitionDto[] = this.getFilteredRecentActions();
    const hasFilters: boolean =
      !!this.state.searchTerm || this.state.selectedAIModelIds.length > 0 || this.state.selectedTags.length > 0;

    const columns: KuikaListTableColumn<IActionDefinitionDto>[] = [
      {
        id: "name",
        header: () => "Name",
        accessorFn: (row: IActionDefinitionDto) => row.name,
        cell: (row: IActionDefinitionDto) => (
          <span
            style={
              {
                "--kuika-list-table-icon-bg": "var(--purple-1)",
                "--kuika-list-table-icon-color": "var(--purple-5)"
              } as React.CSSProperties
            }
          >
            <KuikaListTableNameCell
              name={row.name}
              icon={<KuikaSvg type="datasource-icons" name="ai-action" width={12} height={12} />}
            />
          </span>
        )
      },
      {
        id: "model",
        header: () => "Model",
        width: 160,
        accessorFn: (row: IActionDefinitionDto) => this.getFormattedAIActionModel(row),
        cell: (row: IActionDefinitionDto) => {
          const model: string = this.getFormattedAIActionModel(row);
          if (!model) return "—";
          return <KuikaBadge type="label" size="small" color="purple" content={model} />;
        }
      },
      {
        id: "status",
        header: () => "Status",
        width: 120,
        accessorFn: (row: IActionDefinitionDto) => row.aiActionStatus ?? EAiActionStatus.Published,
        cell: (row: IActionDefinitionDto) => {
          const isDraft: boolean = (row.aiActionStatus ?? EAiActionStatus.Published) === EAiActionStatus.Draft;
          return (
            // The same row can move between Draft and Published (a save from the editor), so the
            // badge morphs to its new word and width instead of cutting.
            <KuikaListTableStatusCell
              label={isDraft ? "Draft" : "Published"}
              color={isDraft ? "yellow" : "green"}
              animateChanges
            />
          );
        }
      },
      {
        id: "created",
        header: () => "Created",
        width: 220,
        // Sorted by WHEN: the cell's caption is the timestamp, so sorting by name
        // would order the column by something it does not show.
        accessorFn: (row: IActionDefinitionDto) => (row.createdAt ? new Date(row.createdAt).getTime() : 0),
        cell: (row: IActionDefinitionDto) => {
          const firstName: string = row.createdUser?.firstName ?? "";
          const lastName: string = row.createdUser?.lastName ?? "";
          const fullName: string = [firstName, lastName].filter(Boolean).join(" ");
          const initials: string = `${firstName[0] ?? ""}${lastName[0] ?? ""}`.toUpperCase();
          return (
            <KuikaListTableUserCell
              caption={row.createdAt ? KMainFunctions.formatRelativeTime(row.createdAt) : undefined}
              name={fullName || undefined}
              photoUrl={row.createdUser?.profilePhotoUrl || undefined}
              initials={initials || undefined}
            />
          );
        }
      }
    ];

    const rowActions: KuikaListTableRowAction<IActionDefinitionDto>[] = [
      {
        key: "edit",
        label: "Edit",
        icon: <KuikaSvg name="edit" width={14} height={14} color="var(--dark-7)" />,
        // A template-based AI Action is owned by its template; editing happens there.
        hidden: (row: IActionDefinitionDto): boolean =>
          !!this.props.sd.getStringifiedAiActionInfo(row.aiActionInfo)?.templateId,
        onSelect: (row: IActionDefinitionDto) => this.handleEditRecentAction(row)
      },
      {
        key: "delete",
        label: "Delete",
        danger: true,
        icon: <KuikaSvg name="trash" width={14} height={14} />,
        onSelect: (row: IActionDefinitionDto) => this.openDeleteActionModal(row)
      }
    ];

    return (
      <div className="ai-action-templates-tab ai-action-templates-tab--without-filters">
        <KuikaListTable<IActionDefinitionDto>
          data={aiActions}
          columns={columns}
          rowActions={rowActions}
          rowKey={(row: IActionDefinitionDto) => row.id}
          emptyTitle={hasFilters ? "No AI Actions match your filters" : "No AI Actions yet"}
          emptyDescription={
            hasFilters
              ? "Try a different search term or clear the filters."
              : "Start from a template or create one from a blank."
          }
        />
      </div>
    );
  };

  renderCreateFromScratch = () => {
    const isSelected: boolean = this.props.selectedTemplateId === "custom";
    return (
      <KuikaCreateCardRow>
        <KuikaCreateCard
          className={isSelected ? "kuika-create-card--selected" : undefined}
          title="Create from a blank"
          description="Start with a blank canvas and build your workflow step by step."
          icon={<KuikaSvg type="datasource-icons" name="ai-action" width={24} height={24} />}
          iconBackground="var(--purple-1)"
          iconColor="var(--purple-5)"
          onClick={this.handleCreateFromScratchClick}
        />
      </KuikaCreateCardRow>
    );
  };

  renderScratchSidebar = () => {
    if (!this.state.isScratchSidebarVisible) return null;
    const closeScratch = () => this.setState({ isScratchSidebarVisible: false });
    const confirmScratch = () => this.startBuildingFromScratch();
    return (
      <div className="ai-template-detail-overlay" onClick={closeScratch}>
        <div className="ai-template-detail-panel" onClick={(e) => e.stopPropagation()}>
          <div className="ai-template-detail-panel__header">
            <div className="ai-template-detail-panel__header-left">
              <KuikaButton type="tertiary" size="sm" isIcon onClick={closeScratch}>
                <KuikaSvg type="common" name="x" width={18} height={18} />
              </KuikaButton>
            </div>
            <div className="ai-template-detail-panel__header-title">
              <p className="ai-template-detail-panel__title">Build from Scratch</p>
            </div>
          </div>
          <div className="ai-template-detail-panel__body">
            <div className="ai-template-detail__header">
              <div className="ai-action-scratch-sidebar__icon">
                <KuikaSvg type="datasource-icons" name="ai-action" width={48} height={48} color="var(--purple-5)" />
              </div>
              <div className="ai-template-detail__header-info">
                <div className="ai-template-detail__description-wrapper">
                  <p className="ai-template-detail__description">
                    Create a custom AI action from scratch. Define how it works, choose a model, and shape the output to
                    fit your needs.
                  </p>
                </div>
              </div>
            </div>
            <div className="ai-template-detail__content">
              <div className="ai-action-scratch-sidebar__features">
                {this.scratchFeatures.map((feature) => (
                  <div className="ai-action-scratch-sidebar__feature" key={feature}>
                    <KuikaSvg type="common" name="check" width={16} height={16} color="var(--green-6)" />
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="ai-template-detail-panel__footer">
            <KuikaButton type="primary" onClick={confirmScratch}>
              Start Building
            </KuikaButton>
          </div>
        </div>
      </div>
    );
  };

  renderSelectedFilters = () => {
    const { selectedAIModelIds, selectedTags, searchTerm } = this.state;
    const hasFilters = selectedAIModelIds.length > 0 || selectedTags.length > 0 || searchTerm;

    if (!hasFilters) return null;

    return (
      <div className="ai-action-selected-filters">
        <div className="ai-action-selected-filters__content">
          {selectedAIModelIds.length > 0 && (
            <div className="ai-action-selected-filters__group">
              <span className="ai-action-selected-filters__label">Model:</span>
              <div className="ai-action-selected-filters__badges">
                {selectedAIModelIds.map((modelId) => {
                  const model = this.state.aiModels.find((m) => m.id === modelId);
                  const modelLabel: string = model?.name || modelId;
                  return (
                    <KuikaBadge
                      key={modelId}
                      content={modelLabel}
                      color="purple"
                      rightIcon={
                        // A real button, so the filter can be dropped from the keyboard too.
                        <button
                          type="button"
                          className="ai-action-selected-filters__badge-remove"
                          aria-label={`Remove ${modelLabel}`}
                          onClick={(e: React.MouseEvent<HTMLButtonElement>): void => {
                            e.stopPropagation();
                            this.handleFilterTemplates(selectedAIModelIds.filter((id) => id !== modelId));
                          }}
                        >
                          ×
                        </button>
                      }
                    />
                  );
                })}
              </div>
            </div>
          )}
        </div>
        <span className="ai-action-selected-filters__clear" onClick={this.handleClearFilters}>
          Clear all
        </span>
      </div>
    );
  };

  render() {
    const isTemplatesTab = this.state.activeTab === "templates";
    const uniqueModels = this.getUniqueModels();
    const recentsModelOptions = this.getRecentsModelOptions();
    const modelOptions = isTemplatesTab
      ? uniqueModels.map((model) => ({
          label: model.name,
          value: model.id
        }))
      : recentsModelOptions;
    // Categories moved out of the toolbar dropdown into a chip row; both tabs share
    // the same `selectedTags`, so the row serves whichever one is showing.
    const categoryOptions: IKuikaFilterChipOption[] = this.getCategoryOptions();
    const allCategoryCount: number = isTemplatesTab
      ? this.getTemplatesBeforeTagFilter().length
      : this.getRecentsBeforeTagFilter().length;
    const hasFilters =
      this.state.selectedAIModelIds.length > 0 || this.state.selectedTags.length > 0 || !!this.state.searchTerm;

    // Templates and Recents are two views of the same page: the new one slides in from the side
    // the selection moved towards instead of cutting.
    const renderTabPane = (tab: string): React.ReactNode =>
      tab === "templates" ? (
        <div
          className={`ai-action-templates-tab ${
            hasFilters ? "ai-action-templates-tab--with-filters" : "ai-action-templates-tab--without-filters"
          }`}
        >
          {this.getFilteredTemplates().length === 0 && !this.state.isTemplatesLoading ? (
            <div className="ai-action-templates-empty">
              <div className="ai-action-templates-empty__icon-container">
                <KuikaSvg type="common" name="search" width={40} height={40} color="var(--dark-3)" />
              </div>
              <h4>No templates found</h4>
              <p>Try adjusting your search or filters</p>
            </div>
          ) : (
            <AIActionTemplates
              aiTemplates={this.getFilteredTemplates()}
              aiModels={this.state.aiModels}
              selectedTemplateId={this.props.selectedTemplateId}
              setTemplateSelected={this.setTemplateSelected}
              isTemplatesLoading={this.state.isTemplatesLoading || !!this.props.isCreatingFromTemplate}
              isEditing={this.props.isEditing}
              onEdit={this.props.onEdit}
              onCreateTemplate={this.props.onCreateTemplate}
            />
          )}
        </div>
      ) : (
        this.renderRecentsTable()
      );

    return (
      <>
        <DeleteModal
          itemName={this.state.actionToDelete?.name || ""}
          triggerModal={this.state.isDeleteActionModalVisible}
          deleteItem={() => this.deleteAIAction()}
          deleteType={"action"}
          closeModal={() =>
            this.setState({
              actionToDelete: null,
              isDeleteActionModalVisible: false
            })
          }
        />
        {this.props.isCreatingCustomTemplate ? (
          <AIActionCustomTemplate
            form={this.props.form}
            isInitial={this.props.isCustomTemplateOnInit}
            isEditingTemplate={this.props.isEditingTemplate}
            model={this.props.model}
            returnType={this.props.returnType}
            aiModels={_.uniqBy(this.state.aiModels, "name")}
            onModelChange={this.props.onModelChange}
            onReturnTypeChange={this.props.onReturnTypeChange}
            requestPromptsFromAI={this.props.requestPromptsFromAI}
            onAIPromptChange={this.props.onAIPromptChange}
          />
        ) : (
          <div className="ai-action-selector">
            <KuikaListPageHeader
              onBack={this.props.onClose}
              backLabel="Back to AI Functions"
              icon={
                <KuikaSvg color="var(--purple-5)" type="datasource-icons" name="ai-action" width={20} height={20} />
              }
              title="Add AI Action"
            />
            {this.state.isScratchEmptyStateVisible ? (
              <CreateScratchEmptyState
                theme="purple"
                iconName="ai-action"
                title="Create an AI Action"
                description="Create a custom AI action from scratch. Define how it works, choose a model, and shape the output to fit your needs."
                learnMoreLink="https://academy.kuika.com/en/content/ai-actions"
                features={this.scratchFeatures}
                onBack={this.closeScratchEmptyState}
                onStart={this.startBuildingFromScratch}
              />
            ) : (
              <>
                {this.renderCreateFromScratch()}
                <KuikaListPageToolbar
                  tabs={
                    <KuikaTabs
                      size="lg"
                      options={[
                        { value: "templates", label: "Templates" },
                        { value: "recents", label: "Recents" }
                      ]}
                      value={this.state.activeTab}
                      onChange={(value) =>
                        this.setState({
                          activeTab: value as "templates" | "recents"
                        })
                      }
                    />
                  }
                  search={
                    <KuikaInput
                      size="md"
                      placeholder="Search..."
                      value={this.state.searchTerm}
                      leftIcon={<KuikaSvg name="search" width={14} height={14} />}
                      onChange={(e, value) => this.handleSearchTemplates(value as string)}
                    />
                  }
                  filters={
                    this.state.activeTab === "templates" || this.state.activeTab === "recents" ? (
                      <>
                        {hasFilters && (
                          <KuikaTooltip content="Clear all filters" position="top">
                            {/* The tooltip hands its trigger a ref, and KuikaButton is a
                                function component without forwardRef — so it wraps a real
                                element, exactly as the other tooltip call sites do. */}
                            <span className="ai-action-clear-filters">
                              <KuikaButton type="tertiary" size="sm" isIcon onClick={this.handleClearFilters}>
                                <KuikaSvg name="circle-x" width={16} height={16} />
                              </KuikaButton>
                            </span>
                          </KuikaTooltip>
                        )}
                        <KuikaSelectbox
                          className={`ai-action-model-filter${
                            this.state.selectedAIModelIds.length > 0 ? " ai-action-filter--active" : ""
                          }`}
                          size="md"
                          mode="multiple"
                          placeholder="All models"
                          value={this.state.selectedAIModelIds}
                          onChange={(selectedIds) => this.handleFilterTemplates(selectedIds as string[])}
                          options={modelOptions}
                          prefix={<KuikaSvg name="filter" width={12} height={12} />}
                          maxTagCount={1}
                          maxTagPlaceholder={(omittedValues: unknown[]) => {
                            const count = omittedValues?.length || 0;
                            if (!count) return null;
                            return <span className="ai-action-templates-tab__filters__tag-counter">{count}</span>;
                          }}
                          showTagCrossIcon={false}
                          showOptionCheckboxes
                          dropdownMatchSelectWidth={false}
                          filterResetTooltip="Clear model filter"
                        />
                      </>
                    ) : undefined
                  }
                />

                {this.state.activeTab === "templates" &&
                  categoryOptions.length > 1 && (
                    // Full-width row of its own rather than a control in the toolbar: a
                    // dozen categories cannot fit beside the search and model filters, and
                    // the point of chips is that the categories and their sizes are legible
                    // WITHOUT opening anything.
                    <KuikaFilterChips
                      className="ai-action-templates-tab__categories"
                      groupLabel="Template category"
                      options={categoryOptions}
                      value={this.state.selectedTags}
                      onChange={this.handleFilterByTags}
                      allLabel="All"
                      allCount={allCategoryCount}
                      data-testid="templateCategoryChips"
                    />
                  )}

                <KuikaTabContent
                  className="ai-action-panel__tab-content"
                  activeKey={this.state.activeTab}
                  order={AI_ACTION_TAB_ORDER}
                  render={renderTabPane}
                />
              </>
            )}

            {/* <div className="ai-action-selector__actions">
              {this.props.onClose && (
                <KuikaButton type="text" onClick={this.props.onClose}>
                  Cancel
                </KuikaButton>
              )}
            </div> */}
          </div>
        )}
        {this.renderScratchSidebar()}
      </>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    return true;
  };
}

const aiActionPanel = withSD<IAiActionPanelProps>(AiActionPanel);
export { aiActionPanel as AiActionPanel };
